import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { supabase } from '../config/supabaseClient';

export default function GroupAdminDashboard({ group }) {
  const [activeTab, setActiveTab] = useState('members'); // members, requests, settings
  const [joinRequests, setJoinRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch members for this specific group
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        if (!group?.id) return;
        
        setLoading(true);
        setError(null);
        
        const { data, error: fetchError } = await supabase
          .from('group_members')
          .select(`
            id,
            role,
            profiles!inner (
              id,
              first_name,
              last_name,
              skill_level,
              avatar_url
            )
          `)
          .eq('group_id', group.id)
          .order('role', { ascending: false });

        if (fetchError) {
          console.error('Error fetching members:', fetchError);
          throw new Error('Failed to load members');
        }
        
        setMembers(data || []);
      } catch (err) {
        console.error('Error fetching members:', err);
        setError(err.message || 'Failed to load members');
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'members') {
      fetchMembers();
    }
  }, [activeTab, group?.id]);

  // Fetch join requests for this specific group
  useEffect(() => {
    const fetchJoinRequests = async () => {
      try {
        if (!group?.id) return;
        
        setLoading(true);
        setError(null);
        
        console.log('Fetching join requests for group:', group.id);

        // First, get all join requests without the profile join to check if they exist
        const { data: basicRequests, error: basicError } = await supabase
          .from('join_requests')
          .select('*')
          .eq('group_id', group.id)
          .eq('status', 'pending');

        if (basicError) {
          console.error('Error fetching basic join requests:', basicError);
          throw basicError;
        }

        console.log('Basic join requests found:', basicRequests);

        // Then get the full data with profiles
        const { data, error: fetchError } = await supabase
          .from('join_requests')
          .select(`
            id,
            created_at,
            status,
            message,
            user_id,
            profiles (
              first_name,
              last_name,
              skill_level
            )
          `)
          .eq('group_id', group.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error('Error fetching join requests with profiles:', fetchError);
          throw fetchError;
        }
        
        console.log('Join requests with profiles:', data);
        setJoinRequests(data || []);
      } catch (err) {
        console.error('Error in fetchJoinRequests:', err);
        setError(err.message || 'Failed to load join requests');
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'requests') {
      fetchJoinRequests();
    }
  }, [activeTab, group?.id]);

  const handleAcceptRequest = async (requestId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the request details first
      const { data: request, error: requestError } = await supabase
        .from('join_requests')
        .select('user_id, status')
        .eq('id', requestId)
        .single();

      if (requestError) {
        console.error('Error fetching request:', requestError);
        throw new Error('Failed to fetch request details');
      }

      if (!request) {
        throw new Error('Request not found');
      }

      if (request.status !== 'pending') {
        throw new Error('Request has already been processed');
      }

      // Check if user is already a member
      const { data: existingMember, error: memberCheckError } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', group.id)
        .eq('user_id', request.user_id)
        .maybeSingle();

      if (memberCheckError && memberCheckError.code !== 'PGRST116') { // PGRST116 means no rows returned
        console.error('Error checking existing membership:', memberCheckError);
        throw new Error('Failed to check existing membership');
      }

      if (existingMember) {
        // User is already a member, just update the request status
        const { error: updateError } = await supabase
          .from('join_requests')
          .update({ status: 'approved' })
          .eq('id', requestId)
          .select();

        if (updateError) throw new Error('Failed to update request status');
      } else {
        // Add user to group members
        const { data: newMember, error: memberError } = await supabase
          .from('group_members')
          .insert({
            group_id: group.id,
            user_id: request.user_id,
            role: 'member'
          })
          .select()
          .single();

        if (memberError) {
          console.error('Error adding member:', memberError);
          throw new Error('Failed to add member to group');
        }

        console.log('Successfully added member:', newMember);

        // Update request status
        const { error: updateError } = await supabase
          .from('join_requests')
          .update({ status: 'approved' })
          .eq('id', requestId)
          .select();

        if (updateError) {
          console.error('Error updating request status:', updateError);
          throw new Error('Failed to update request status');
        }
      }

      // Update local state
      setJoinRequests(prev => prev.filter(req => req.id !== requestId));

      // Refresh members list
      const { data: updatedMembers, error: membersError } = await supabase
        .from('group_members')
        .select(`
          id,
          role,
          profiles!inner (
            id,
            first_name,
            last_name,
            skill_level,
            avatar_url
          )
        `)
        .eq('group_id', group.id)
        .order('role', { ascending: false });

      if (membersError) {
        console.error('Error refreshing members:', membersError);
      } else {
        setMembers(updatedMembers || []);
      }
    } catch (error) {
      console.error('Error accepting request:', error);
      setError(error.message || 'Failed to accept request');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      setLoading(true);
      
      const { error: updateError } = await supabase
        .from('join_requests')
        .update({ status: 'rejected' })
        .eq('id', requestId);

      if (updateError) throw updateError;

      // Update local state
      setJoinRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (error) {
      console.error('Error rejecting request:', error);
      setError('Failed to reject request');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      setLoading(true);
      
      const { error: deleteError } = await supabase
        .from('group_members')
        .delete()
        .eq('id', memberId);

      if (deleteError) throw deleteError;

      // Update local state
      setMembers(prev => prev.filter(member => member.id !== memberId));
    } catch (error) {
      console.error('Error removing member:', error);
      setError('Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = (newSettings) => {
    // In production, this would be an API call
    console.log('Updating settings:', newSettings);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 font-medium text-sm border-b-2 ${
              activeTab === 'members'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Member Management
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2 font-medium text-sm border-b-2 ${
              activeTab === 'requests'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Join Requests
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium text-sm border-b-2 ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Settings
          </button>
        </nav>
      </div>

      {/* Content Sections */}
      <div className="p-4">
        {/* Members Management */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Member Management</h3>
            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-lg">
                {error}
              </div>
            )}
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : members.length === 0 ? (
              <div className="text-center py-8">
                <div className="font-bold text-2xl mb-2">
                  <div className="text-black">No</div>
                  <div className="text-[#0D9488]">Members</div>
                </div>
                <p className="text-gray-600">
                  Invite players to join your group!
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {members.map(member => (
                  <div key={member.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                        {member.profiles.avatar_url ? (
                          <img
                            src={member.profiles.avatar_url}
                            alt={`${member.profiles.first_name}'s avatar`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <svg
                            className="w-6 h-6 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">
                          {member.profiles.first_name} {member.profiles.last_name}
                          {member.role === 'admin' && (
                            <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                              Admin
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">NTRP {member.profiles.skill_level}</p>
                      </div>
                    </div>
                    {member.role !== 'admin' && (
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                        disabled={loading}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Join Requests */}
        {activeTab === 'requests' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Join Requests</h3>
            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-lg">
                {error}
              </div>
            )}
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : joinRequests.length === 0 ? (
              <div className="text-center py-8">
                <div className="font-bold text-2xl mb-2">
                  <div className="text-black">No</div>
                  <div className="text-[#0D9488]">Pending Requests</div>
                </div>
                <p className="text-gray-600">
                  No one has requested to join your group yet.
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {joinRequests.map(request => (
                  <div key={request.id} className="py-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium">
                          {request.profiles.first_name} {request.profiles.last_name}
                        </p>
                        <p className="text-sm text-gray-500">NTRP {request.profiles.skill_level}</p>
                        <p className="text-sm text-gray-500">
                          Requested {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleAcceptRequest(request.id)}
                          className="px-3 py-1 bg-[#90B77D] text-white rounded-md text-sm hover:bg-[#7a9c69] transition"
                          disabled={loading}
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => handleRejectRequest(request.id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-sm hover:bg-red-200 transition"
                          disabled={loading}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                    {request.message && (
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                        {request.message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Group Settings */}
        {activeTab === 'settings' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Group Settings</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateSettings({
                // Get form values and update
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Group Name</label>
                  <input
                    type="text"
                    defaultValue={group.name}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    defaultValue={group.description}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Visibility</label>
                  <select
                    defaultValue={group.visibility}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#90B77D] text-white font-semibold rounded-lg hover:bg-[#7a9c69] transition"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

GroupAdminDashboard.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    visibility: PropTypes.string.isRequired,
    admin_id: PropTypes.string.isRequired
  }).isRequired
}; 